
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import fs from 'fs'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import {useRouter} from 'next/router'
import {join} from 'path'
import {getPlaiceholder} from 'plaiceholder'
import Button from '../../components/atoms/button'
import Card from '../../components/atoms/card'
import Container from '../../components/atoms/container'
import GridContainer from '../../components/atoms/grid-container'
import GridItem from '../../components/atoms/grid-item'
import Heading from '../../components/atoms/heading'
import ExternalLinkIcon from '../../components/atoms/icons/external-link-icon'
import LoadableImage from '../../components/atoms/loadable-image'
import Paragraph from '../../components/atoms/paragraph'
import Avatar from '../../components/molecules/avatar'
import CallToAction from '../../components/molecules/call-to-action'
import InfoBox from '../../components/molecules/info-box'
import Testimonial from '../../components/molecules/testimonial'
import CaseStudySection from '../../components/templates/case-study-section'
import {
  FooterDataStateProps,
  FooterProvider,
} from '../../contexts/footer-context'
import PageLayout from '../../layouts/page-layout'
import defaultImageLoader from '../../lib/default-image-loader'
import fetchGeneralSettings from '../../lib/fetch-general-settings'
import fetchSocialLinks from '../../lib/fetch-social-links'
import mapRawCaseStudyToCaseStudy from '../../lib/map-raw-case-study-to-case-study'
import mapRawTestimonialToTestimonial from '../../lib/map-raw-testimonial-to-testimonial'
import removeDiacritics from '../../lib/remove-diacritics'
import bishopSymbol from '../../public/assets/bishop-logo-symbol.png'
import dotGridDark from '../../public/assets/dense-dot-grid-dark-gray.png'
import dotGrid from '../../public/assets/dense-dot-grid-light-gray.png'
import {
  CaseStudy as CaseStudyDataProps,
  Testimonial as TestimonialDataProps,
} from '../../types'

export interface CaseStudyDetailsProps extends FooterDataStateProps {
  data?: CaseStudyDataProps
}

export default function CaseStudyDetails({
  data,
  social,
  general,
}: CaseStudyDetailsProps) {
  const router = useRouter()
  const {t} = useTranslation('case-studies')

  if (router.isFallback || !data) {
    return <></>
  }

  return (
    <FooterProvider value={{social, general}}>
      <PageLayout
        className="grid pt-19 md:pt-20 pb-24 gap-16 lg:gap-24"
        disableDefaultPadding
        title={data.title}
        description={data.shortDescription}
        openGraph={{
          images: [{url: data.previewImage || bishopSymbol.src}],
          url: `case-studies/${data.slug}`,
        }}
        prefixImageUrl={!data.previewImage}
        type={'article'}
        headerProps={{disableScrollBehaviour: true}}
      >
        <section
          className="pt-12 pb-16 lg:py-20 overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(to top right, ${data.coverGradientStart}, ${data.coverGradientEnd})`,
          }}
        >
          <Container>
            <GridContainer
              disableDefaultColumns
              className="gap-8 grid-cols-1 lg:grid-cols-2 text-white items-center"
            >
              <GridItem className="grid relative order-1 justify-items-center lg:justify-items-start lg:order-0 gap-4">
                <LoadableImage
                  src={data.logo.image}
                  alt={data.logo.alt}
                  width={data.logo.width}
                  height={data.logo.height}
                  quality={100}
                  placeholderColor="transparent"
                  borderRadius="rounded-none"
                  wrapperClassName="max-h-16 justify-self-center lg:justify-self-start logo"
                  className="object-center lg:object-left object-contain"
                  disableDefaultAnimation
                  priority
                />

                <div className="grid">
                  <Heading
                    disableMaxWidth
                    className="text-white text-center lg:text-left"
                  >
                    {data.title}
                  </Heading>

                  <Paragraph color="light" className="text-center lg:text-left">
                    {data.description}
                  </Paragraph>
                </div>

                {data.url && (
                  <Button
                    renderAsLink
                    href={data.url}
                    rel="noopener noreferrer"
                    className="grid grid-flow-col gap-2 items-center justify-self-center lg:justify-self-start"
                    environment="dark"
                    target="_blank"
                  >
                    {t('cover.open-website')}

                    <ExternalLinkIcon className="w-5 h-5" />
                  </Button>
                )}
              </GridItem>

              <GridItem className="relative order-2 max-w-none sm:max-w-xl lg:max-w-none justify-self-center w-full">
                <div className="absolute -top-4 -left-2 lg:-top-4 lg:-left-1 z-0 w-36">
                  <Image
                    src={dotGrid}
                    alt="A grid of dots"
                    width={144}
                    height={229.61}
                    quality={1}
                    className="opacity-50"
                    loader={defaultImageLoader}
                  />
                </div>

                <div className="absolute -bottom-5 -right-2 lg:-right-1 z-0 w-36">
                  <Image
                    src={dotGrid}
                    alt="A grid of dots"
                    width={144}
                    height={229.61}
                    quality={1}
                    className="opacity-50"
                    loader={defaultImageLoader}
                  />
                </div>

                <LoadableImage
                  src={data.coverPhoto}
                  alt={data.coverPhotoAlt}
                  width={1440}
                  height={980}
                  quality={95}
                  layout="responsive"
                  borderRadius="rounded-lg"
                  placeholderColor="light"
                  wrapperClassName="shadow-card-hover"
                  sizes="(max-width: 576px) 576px, 1080px"
                  priority
                />
              </GridItem>
            </GridContainer>
          </Container>
        </section>

        {data.challenges && (
          <Container
            component="section"
            className="grid gap-8 justify-items-center"
          >
            <Heading variant="h2" className="text-left md:text-center">
              {t('challenges.title')}
            </Heading>

            <GridContainer
              disableDefaultColumns
              className="md:grid-cols-1 lg:grid-cols-3 gap-y-8 gap-x-12 w-full"
            >
              {data.challenges.map(({title, body}) => (
                <InfoBox title={title} key={title}>
                  {body}
                </InfoBox>
              ))}
            </GridContainer>
          </Container>
        )}

        {data.solutions && (
          <section className="py-12 bg-highlighted overflow-hidden">
            <Container className="grid gap-8 justify-items-center">
              <Heading variant="h2" className="hidden lg:block text-center">
                {t('solutions.title')}
              </Heading>

              <GridContainer
                disableDefaultColumns
                className="gap-8 grid-cols-1 lg:grid-cols-2 items-center"
              >
                <GridItem
                  disableDefaultPadding
                  className="order-1 lg:order-0 grid gap-8"
                >
                  <Heading
                    variant="h2"
                    disableMaxWidth
                    className="block lg:hidden text-center"
                  >
                    {t('solutions.title')}
                  </Heading>

                  <GridContainer
                    disableDefaultColumns
                    className="gap-y-8 gap-x-12 w-full"
                  >
                    {data.solutions.map(({title, body}) => (
                      <InfoBox title={title} key={title} disableHeadingMaxWidth>
                        {body}
                      </InfoBox>
                    ))}
                  </GridContainer>
                </GridItem>

                <GridItem className="order-0 lg:order-1 relative max-w-none sm:max-w-xl lg:max-w-none justify-self-center w-full">
                  <div className="absolute -top-4 -right-2 lg:-top-4 lg:-right-1 z-0 w-36">
                    <Image
                      src={dotGridDark}
                      alt="A grid of dots"
                      width={144}
                      height={229.61}
                      quality={1}
                      className="opacity-50"
                      loader={defaultImageLoader}
                    />
                  </div>

                  <div className="absolute -bottom-5 -left-2 lg:-left-1 z-0 w-36">
                    <Image
                      src={dotGridDark}
                      alt="A grid of dots"
                      width={144}
                      height={229.61}
                      quality={1}
                      className="opacity-50"
                      loader={defaultImageLoader}
                    />
                  </div>

                  <LoadableImage
                    src={data.solutionsPhoto}
                    alt={data.solutionsPhotoAlt}
                    width={1440}
                    height={980}
                    quality={100}
                    wrapperClassName="shadow-card-hover"
                    borderRadius="rounded-lg"
                    layout="responsive"
                    sizes="(max-width: 576px) 576px, (max-width: 640px) 828px, 640px"
                    placeholderBorderRadius="rounded-2xl md:rounded-3xl"
                  />
                </GridItem>
              </GridContainer>
            </Container>
          </section>
        )}

        {data.results && (
          <Container
            component="section"
            className="grid gap-8 justify-items-center w-full"
          >
            <Heading variant="h2" className="text-center">
              {t('results.title')}
            </Heading>

            <GridContainer
              disableDefaultColumns
              className="gap-y-8 gap-x-12 w-full"
            >
              {data.results.map(({title, body}) => (
                <InfoBox title={title} key={title} disableHeadingMaxWidth>
                  {body}
                </InfoBox>
              ))}
            </GridContainer>

            {data.resultsPhoto && data.resultsPhotoAlt && (
              <Card
                borderRadius="rounded-2xl md:rounded-3xl"
                padding="none"
                className="overflow-hidden w-full mt-8 hidden sm:block"
              >
                <LoadableImage
                  src={data.resultsPhoto}
                  alt={data.resultsPhotoAlt}
                  layout="responsive"
                  width={1440}
                  height={900}
                  quality={100}
                  borderRadius="rounded-2xl md:rounded-3xl"
                  sizes="(max-width: 576px) 750px, (max-width: 640px) 1080px, 1920px"
                  className="overflow-hidden"
                  disableDefaultAnimation
                />
              </Card>
            )}
          </Container>
        )}

        {data.testimonials && data.testimonials.length > 0 && (
          <section className="py-12 bg-highlighted overflow-hidden">
            <Container className="grid gap-4 justify-items-center">
              <Heading variant="h2" className="text-center">
                {t('testimonials.title', {count: data.testimonials.length})}
              </Heading>

              <GridContainer
                disableDefaultColumns
                className="w-full md:w-4/5 mx-auto gap-y-8 gap-x-12"
              >
                {data.testimonials.map(
                  ({attestant, role, testimonial, photo}) => (
                    <Testimonial
                      avatar={
                        photo && (
                          <Avatar
                            src={photo}
                            alt={attestant}
                            size={75}
                            borderRadius="small"
                            disableDefaultAnimation
                          />
                        )
                      }
                      attestant={attestant}
                      role={role}
                      key={`${attestant}-${role}`}
                      paragraphProps={{
                        className: 'text-lg md:text-xl mb-2',
                      }}
                    >
                      {testimonial}
                    </Testimonial>
                  ),
                )}
              </GridContainer>
            </Container>
          </section>
        )}

        <Container>
          <CallToAction
            title={
              data.categories && data.categories.includes('Medical')
                ? t('contact.title')
                : t('contact.generic-title')
            }
            callToActionText={t('common:navigation.contact-us')}
            href="/contact-us"
          >
            {t('contact.body')}
          </CallToAction>
        </Container>

        {data.highlightedCaseStudies &&
          data.highlightedCaseStudies.length > 0 && (
            <CaseStudySection
              title={t('case-studies.title')}
              caseStudies={data.highlightedCaseStudies}
            />
          )}
      </PageLayout>
    </FooterProvider>
  )
}

 async function _getStaticProps({...ctx}) {
  try {
    const {slug} = ctx.params
    const parseFrontMatter = await import('../../lib/parse-front-matter')
    const content = await import(`../../content/case-studies/${slug}.md`)
    const {
      attributes: {logo, ...data},
    } = parseFrontMatter.default<any>(content.default)

    const caseStudies: Array<any> = data['highlighted-case-studies']

    let testimonials: Array<TestimonialDataProps> = []
    let highlightedCaseStudies: Array<CaseStudyDataProps> = []

    const convertedLogo = await getPlaiceholder(logo)

    if (data.testimonials) {
      const testimonialList = await Promise.all(
        data.testimonials.map(
          (attestant: string) =>
            import(
              `../../content/testimonials/${removeDiacritics(attestant)
                .toLowerCase()
                .replace(/\./gi, '')
                .replace(/ /gi, '-')}.md`
            ),
        ),
      )

      testimonials = testimonialList.map(mapRawTestimonialToTestimonial)
    }

    if (caseStudies) {
      const availableCaseStudies = await Promise.all(
        caseStudies.map(
          caseStudy => import(`../../content/case-studies/${caseStudy}.md`),
        ),
      )

      highlightedCaseStudies = await Promise.all(
        availableCaseStudies
          .map(
            content =>
              parseFrontMatter.default<any>(content.default).attributes,
          )
          .map(async ({logo, ...data}) => {
            const convertedLogo = await getPlaiceholder(logo)

            return mapRawCaseStudyToCaseStudy({logo: convertedLogo, ...data})
          }),
      )
    }

    return {
      props: {
        data: {
          ...mapRawCaseStudyToCaseStudy({logo: convertedLogo, ...data}),
          testimonials,
          highlightedCaseStudies,
        },
        social: fetchSocialLinks(),
        general: fetchGeneralSettings(),
      },
    }
  } catch (error) {
    return {
      notFound: true,
    }
  }
}

export async function getStaticPaths() {
  const CASE_STUDIES = join(process.cwd(), 'content/case-studies')
  const paths = fs
    .readdirSync(CASE_STUDIES)
    .map(path => path.replace(/\.mdx?$/, ''))
    .map(slug => ({params: {slug}}))

  return {
    fallback: true,
    paths,
  }
}


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/case-studies/[slug]',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  